'use client';
import React, { FC, HTMLAttributes, useEffect, useState } from 'react';
import Link from 'next/link';

import { ICategoryDictionary } from '@/dictionaries/pages/category/types';
import { Experience } from '@/services/experienceTypes';
import useLocalStorage from '@/hooks/useLocalStorage';

import { ExperienceType, ShortList } from './ExperienceType';
import { CategoryFilter } from './CategoryFilter';

import { AnimatedLink } from '../AnimatedLink';
import Button from '../Button';

import { IoIosArrowBack } from 'react-icons/io';

interface ICategorySectionsProps extends HTMLAttributes<HTMLDivElement> {
  dict: ICategoryDictionary;
  lang: string;
  experiences: Experience[];
  categoryTitle: string;
  types: {
    id: string;
    title: string;
    description: string;
  }[];
}

export const CategorySections: FC<ICategorySectionsProps> = ({
  dict,
  lang,
  experiences,
  categoryTitle,
  types,
}) => {
  const [chosenTypes, setChosenTypes] = useState<string[]>([]);
  const [chosenLanguages, setChosenLanguages] = useState<string[]>([]);
  const [chosenZone, setChosenZone] = useState({
    label: `${dict.filter.zoneOptions[0].label}`,
    value: 'any',
  });
  const [chosenPrice, setChosenPrice] = useState({
    minPrice: 0,
    maxPrice: 3000,
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );

  const typesFiltered = types?.filter((item) =>
    chosenTypes.length === 0 ? item : chosenTypes.includes(item.id),
  );

  const experienceTypeList = typesFiltered?.map((item) => {
    let filteredList = experiences.filter(
      (experience) => experience.category.id === Number(item.id),
    );

    if (chosenZone.value !== 'any') {
      const zoneFilteredList = filteredList.filter(
        (exp) => exp.zone !== null && exp.zone.code === chosenZone.value,
      );
      filteredList = zoneFilteredList;
    }

    if (chosenLanguages.length > 0) {
      const languageFilteredList = filteredList.filter((exp) =>
        exp.languages.some((language) =>
          chosenLanguages.includes(language.code),
        ),
      );
      filteredList = languageFilteredList;
    }

    if (chosenPrice.minPrice !== 0 || chosenPrice.maxPrice !== 3000) {
      const isPriceInRange = (price: string) => {
        const numericPrice = parseFloat(price);
        return (
          numericPrice >= chosenPrice.minPrice &&
          numericPrice <= chosenPrice.maxPrice
        );
      };

      const priceFilteredList = filteredList.filter((exp) => {
        const allPrices: {
          minParticipants: number;
          maxParticipants: number;
          price: string;
        }[] = [];

        exp.optionMaps.forEach((optionMap) => {
          if (optionMap.prices) {
            allPrices.push(...optionMap.prices);
          }
        });

        return allPrices.some((priceObj) => isPriceInRange(priceObj.price));
      });

      filteredList = priceFilteredList;
    }

    return { type: item, experiences: filteredList };
  });

  const totalExperiences = experienceTypeList?.reduce(
    (total, item) => total + item.experiences.length,
    0,
  );

  const experienceType = experienceTypeList?.map((item) => {
    if (item.experiences.length !== 0) {
      return (
        <ExperienceType
          key={item.type.id}
          dict={dict}
          lang={lang}
          title={item.type.title}
          description={item.type.description}
          experiences={item.experiences}
          favourites={favourites}
          setFavourites={setFavourites}
        />
      );
    }
  });

  useEffect(() => {
    setChosenTypes(types?.map((item) => item.id) as string[]);
  }, [types]);

  useEffect(() => {
    const handleScroll = () => {
      const typeElement = document.querySelector('#type');

      if (!typeElement) return;

      const typeRect = typeElement.getBoundingClientRect();

      if (typeRect.top > 144) {
        setIsSticky(false);
      }
    };

    var root = document.getElementsByTagName('html')[0];
    document.body.classList.remove('no-scroll');
    root.classList.remove('no-scroll');

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const contentElement = document.querySelector('#sticky');

      if (!contentElement) return;

      const contentRect = contentElement.getBoundingClientRect();

      if (contentRect.top <= 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  return (
    <>
      <div
        id="sticky"
        className={`hidden desktop:flex flex-col sticky desktop:static -top-[60px] -mx-10 bg-white z-[999] ${
          isSticky && 'shadow-[0_5px_8px_-6px_grey] desktop:shadow-none'
        }`}>
        {totalExperiences !== 0 && (
          <div className="mb-12 mx-10">
            <span className="text-gray-700 font-text">
              {totalExperiences} {dict.experiencesFound}
            </span>
          </div>
        )}

        <div>
          <div
            className="hidden desktop:block absolute top-[35%] left-0 -rotate-90 -translate-x-[41%] translate-y-[420%]"
            onClick={() => setOpenFilter(true)}>
            <AnimatedLink className="w-fit" href="#">
              {dict.filterSearch}
            </AnimatedLink>
          </div>

          <div className="desktop:hidden flex flex-col mb-10 mx-10 gap-4">
            <div className="flex items-center gap-8 px-[15px] desktop:px-10">
              <Link href={'/sicily/experiences'}>
                <IoIosArrowBack className="h-6 w-6 text-gray-500" />
              </Link>
              <h3 className="text-2xl font-noe">{categoryTitle}</h3>
            </div>
            <div className="flex w-full gap-8">
              <Button
                variant={'outline'}
                className="h-[50px]"
                onClick={() => setOpenFilter(true)}>
                {dict.filterButton}
              </Button>
              <Button className="h-[50px]" onClick={() => setOpenFilter(true)}>
                {dict.orderButton}
              </Button>
            </div>
          </div>

          {openFilter && (
            <CategoryFilter
              lang={lang}
              dict={dict}
              category={categoryTitle}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              typesIds={chosenTypes}
              allTypesIds={types?.map((item) => item.id) as string[]}
              setChosenTypes={setChosenTypes}
              chosenLanguages={chosenLanguages}
              setChosenLanguages={setChosenLanguages}
              chosenZone={chosenZone}
              setChosenZone={setChosenZone}
              chosenPrice={chosenPrice}
              setChosenPrice={setChosenPrice}
            />
          )}
        </div>
      </div>

      {totalExperiences !== 0 ? (
        experienceType
      ) : (
        <div className="desktop:h-[500px] desktop:mt-8">
          <p>{dict.noResultsFound}</p>
        </div>
      )}
    </>
  );
};
